/**
 * StickySearchBar — Iter 80.
 *
 * Compact search bar pinned right under the sticky header once the hero
 * search scrolls out of view on the homepage. One tap smooth-scrolls back
 * to the full hero search; the amber button jumps straight to /cars.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, CalendarDays } from "lucide-react";

export default function StickySearchBar({ heroRef }) {
  const [show, setShow] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const el = heroRef?.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const obs = new IntersectionObserver(
      ([entry]) => setShow(!entry.isIntersecting),
      { rootMargin: "-64px 0px 0px 0px", threshold: 0 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [heroRef]);

  const backToSearch = () => {
    heroRef?.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div
      className={`fixed inset-x-0 top-16 z-30 transition-all duration-300 ${
        show ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0 pointer-events-none"
      }`}
      data-testid="sticky-search-bar"
      aria-hidden={!show}
    >
      <div className="bg-amber-500 shadow-lg shadow-amber-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center gap-2">
          <button
            type="button"
            onClick={backToSearch}
            className="flex-1 min-w-0 flex items-center gap-2.5 bg-white rounded-full px-4 py-2 text-left hover:shadow-md transition group"
            data-testid="sticky-search-open"
            title="Back to the search"
          >
            <Search className="h-4 w-4 text-amber-600 shrink-0" />
            <span className="text-sm font-medium text-neutral-800 truncate">
              Find your car
              <span className="hidden sm:inline text-neutral-400 font-normal"> — pick-up city, dates &amp; time</span>
            </span>
            <span className="ml-auto hidden md:flex items-center gap-3 text-xs text-neutral-400 shrink-0">
              <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> Where</span>
              <span className="inline-flex items-center gap-1"><CalendarDays className="h-3 w-3" /> When</span>
            </span>
          </button>
          <button
            type="button"
            onClick={() => navigate("/cars")}
            className="shrink-0 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-5 py-2 transition"
            data-testid="sticky-search-browse"
          >
            Search cars
          </button>
        </div>
      </div>
    </div>
  );
}
